import { LocaleImage, LocaleAnchor } from '@/i18n/router';
import { LAUNCH_DATE, tpchCopy } from '@/data/hardcharger-launch';
import { useLocale, useMessages } from '@/i18n/LocaleProvider';
import { messages, type Messages } from '@/i18n/catalogs/editorial/news-section';
import React from 'react';

import { Link } from '@/i18n/router';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, ArrowRight, MapPin, Mic, Sparkles, Rocket } from 'lucide-react';
import SectionHeading from '@/components/SectionHeading';

export interface NewsItem {
  id: string;
  title: string;
  excerpt: string;
  date: string;
  category: 'Event' | 'Announcement' | 'Release';
  image: string;
  images?: string[];
  imageStyle?: 'cover' | 'contain';
  link?: string;
  isExternal?: boolean;
  location?: string;
  speaker?: string;
  features?: string[];
}

export const getNewsItems = (m: Messages): NewsItem[] => {
  const english = m === messages.en;
  const tpch = tpchCopy(english ? 'en' : 'pt-BR');
  return [
  {
    id: 'hackernoon-front-page-hardcharger',
    title: english
      ? "Hardcharger Makes the Front Page of HackerNoon"
      : "O Hardcharger chega à página inicial do HackerNoon",
    excerpt: english
      ? "Shawn Gordon's “What the Heck is Hardcharger?” was featured on HackerNoon's front page on October 30, 2026. He walks through why Hardcharger traded Arrow Flight SQL for Chargewire, the security built into the protocol and the formerly Enterprise features now included, then shares a local DuckDB file on GizmoData Cloud and queries it from Claude over MCP. The article is republished on our blog with his permission."
      : "O artigo “What the Heck is Hardcharger?”, de Shawn Gordon, ganhou destaque na página inicial do HackerNoon em 30 de outubro de 2026. Ele explica por que o Hardcharger trocou o Arrow Flight SQL pelo Chargewire, a segurança embutida no protocolo e os recursos antes Enterprise agora incluídos, e depois compartilha um arquivo DuckDB local no GizmoData Cloud e o consulta pelo Claude via MCP. O artigo foi republicado no nosso blog com a autorização dele.",
    date: '2026-10-30',
    category: 'Announcement',
    image: '/hackernoon-front-page-hardcharger.webp',
    imageStyle: 'contain',
    link: '/news#hackernoon-front-page-hardcharger',
    isExternal: false,
    features: english
      ? ["HackerNoon Front Page", "Guest Article by Shawn Gordon", "Chargewire: HTTP + Arrow", "Claude over MCP on GizmoData Cloud"]
      : ["Página inicial do HackerNoon", "Artigo de convidado de Shawn Gordon", "Chargewire: HTTP + Arrow", "Claude via MCP no GizmoData Cloud"],
  },
  {
    id: 'hardcharger-launch',
    title: english
      ? "Hardcharger™ Launches: The Open-Source DuckDB 2.0 Server, Every Feature Included"
      : "Lançamento do Hardcharger™: o servidor de código aberto para o DuckDB 2.0, com todos os recursos incluídos",
    excerpt: english
      ? `GizmoData launches Hardcharger, an Apache-2.0 server for DuckDB 2.0 and the successor to GizmoSQL. It speaks Chargewire, a new open protocol for SQL over HTTP with Apache Arrow results, and includes access control, SSO, mTLS, an audit log, instrumentation and a built-in web UI, with no license keys. On one AWS Graviton5 machine it ran 22 queries derived from TPC-H over 1 TB in ${tpch.hcSeconds} seconds, about ${tpch.hcCents} cents of compute.`
      : `A GizmoData lança o Hardcharger, um servidor Apache-2.0 para o DuckDB 2.0 e sucessor do GizmoSQL. Ele fala Chargewire, um novo protocolo aberto para SQL por HTTP com resultados em Apache Arrow, e inclui controle de acesso, SSO, mTLS, log de auditoria, instrumentação e uma interface web integrada, sem chaves de licença. Em uma única máquina AWS Graviton5, ele executou 22 consultas derivadas do TPC-H sobre 1 TB em ${tpch.hcSeconds} segundos, cerca de ${tpch.hcCents} centavos de dólar de computação.`,
    date: LAUNCH_DATE,
    category: 'Release',
    image: '/logos/hardcharger.webp',
    images: ['/logos/hardcharger.webp', '/logos/gizmodata.png'],
    imageStyle: 'contain',
    link: '/news#hardcharger-launch',
    isExternal: false,
    features: english
      ? ['DuckDB 2.0 Server', 'Chargewire: HTTP + Arrow', 'Every Feature Included', 'Apache-2.0 Open Source']
      : ['Servidor para o DuckDB 2.0', 'Chargewire: HTTP + Arrow', 'Todos os recursos incluídos', 'Código aberto Apache-2.0'],
  },
  {
    id: 'chargewire-1-0',
    title: english
      ? "Chargewire 1.0: An Open Protocol for SQL over HTTP, Powered by Apache Arrow"
      : "Chargewire 1.0: um protocolo aberto para SQL por HTTP, com Apache Arrow",
    excerpt: english
      ? "Chargewire, the protocol under Hardcharger, reaches 1.0. Any HTTP client can run SQL and get Apache Arrow back: no gRPC, no protobuf, no generated code. The specification, an OpenAPI 3.1 description and a 282-test conformance suite are open source under Apache-2.0, with ADBC, JDBC and JavaScript clients."
      : "O Chargewire, o protocolo por trás do Hardcharger, chega à versão 1.0. Qualquer cliente HTTP pode executar SQL e receber Apache Arrow: sem gRPC, sem protobuf, sem código gerado. A especificação, uma descrição OpenAPI 3.1 e uma suíte de conformidade com 282 testes são de código aberto sob a licença Apache-2.0, com clientes ADBC, JDBC e JavaScript.",
    date: LAUNCH_DATE,
    category: 'Release',
    image: '/logos/chargewire.svg',
    images: ['/logos/chargewire.svg', '/logos/gizmodata.png'],
    imageStyle: 'contain',
    link: '/news#chargewire-1-0',
    isExternal: false,
    features: english
      ? ['Version 1.0: Additive From Here', 'Arrow IPC over Plain HTTP', 'OpenAPI 3.1 + 282 Conformance Tests', 'Apache-2.0, Engine-Neutral']
      : ['Versão 1.0: só acréscimos daqui em diante', 'Arrow IPC por HTTP comum', 'OpenAPI 3.1 + 282 testes de conformidade', 'Apache-2.0, independente de mecanismo'],
  },
  {
    id: 'gizmosql-edge-channel-duckdb-2',
    title: english ? 'GizmoSQL v1.41.0: The Experimental Edge Channel Brings DuckDB 2.0 Early' : 'GizmoSQL v1.41.0: o canal experimental Edge traz o DuckDB 2.0 antes do lançamento',
    excerpt: english
      ? 'GizmoSQL v1.41.0 adds a third release channel, Edge, built on the next DuckDB major before it ships: today a DuckDB 2.0 pre-release. It is experimental and not for production workloads; it is there so you can try your workloads, clients and data on DuckDB 2.0 early. Install it with the one-line installer, Homebrew, Docker or the Windows MSI, side by side with Stable and LTS, or launch an Edge cluster on GizmoData Cloud.'
      : 'O GizmoSQL v1.41.0 adiciona um terceiro canal de lançamento, Edge, baseado na próxima versão principal do DuckDB antes do lançamento — atualmente, uma prévia do DuckDB 2.0. É experimental e não deve ser usado em produção: permite testar suas cargas de trabalho, clientes e dados no DuckDB 2.0 antecipadamente. Instale pelo instalador de uma linha, Homebrew, Docker ou MSI do Windows, lado a lado com Stable e LTS, ou crie um cluster Edge no GizmoData Cloud.',
    date: '2026-10-06',
    category: 'Release',
    image: '/logos/duckdb.svg',
    images: ['/logos/gizmosql.png', '/logos/duckdb.svg'],
    imageStyle: 'contain',
    link: '/news#gizmosql-edge-channel-duckdb-2',
    isExternal: false,
    features: english
      ? ['DuckDB 2.0 Pre-release', 'Experimental: Not for Production', 'VARIANT over Arrow Flight SQL', 'Self-Hosted or on GizmoData Cloud']
      : ['Prévia do DuckDB 2.0', 'Experimental: não use em produção', 'VARIANT via Arrow Flight SQL', 'Auto-hospedado ou no GizmoData Cloud'],
  },
  {
    id: 'gizmodata-cloud-generally-available',
    title: m.m000,
    excerpt:
      m.m001,
    date: '2026-09-28',
    category: 'Release',
    image: '/gizmodata-cloud-launch-blog.svg',
    imageStyle: 'contain',
    link: '/news#gizmodata-cloud-generally-available',
    isExternal: false,
    features: [
      m.m002,
      m.m003,
      m.m004,
      m.m005,
    ],
  },
  {
    id: 'gizmosql-v1-39-metrics-eager-execution',
    title: m.m006,
    excerpt:
      m.m007,
    date: '2026-09-14',
    category: 'Release',
    image: '/logos/grafana.svg',
    images: ['/logos/gizmosql.png', '/logos/grafana.svg'],
    imageStyle: 'contain',
    link: '/news#gizmosql-v1-39-metrics-eager-execution',
    isExternal: false,
    features: [
      m.m008,
      m.m009,
      m.m010,
      m.m011,
    ],
  },
  {
    id: 'chris-wones-joins-gizmodata',
    title: m.m012,
    excerpt:
      m.m013,
    date: '2026-09-08',
    category: 'Announcement',
    image: '/team/chris-wones.webp',
    imageStyle: 'contain',
    link: '/news#chris-wones-joins-gizmodata',
    isExternal: false,
    features: [
      m.m014,
      m.m015,
      m.m016,
      m.m017,
    ],
  },
  {
    id: 'gizmosql-mcp-server',
    title: m.m018,
    excerpt:
      m.m019,
    date: '2026-09-04',
    category: 'Release',
    image: '/logos/mcp.svg',
    images: ['/logos/gizmosql.png', '/logos/mcp.svg'],
    imageStyle: 'contain',
    link: '/news#gizmosql-mcp-server',
    isExternal: false,
    features: [
      m.m020,
      m.m021,
      m.m022,
      m.m023,
    ],
  },
  {
    id: 'adbc-driver-suite',
    title: m.m024,
    excerpt:
      m.m025,
    date: '2026-08-27',
    category: 'Release',
    image: '/adbc-drivers-badge.svg',
    images: ['/logos/gizmosql.png', '/adbc-drivers-badge.svg'],
    imageStyle: 'contain',
    link: '/blog/gizmodata-adbc-driver-suite',
    isExternal: false,
    features: [
      m.m026,
      m.m027,
      m.m028,
      m.m029,
    ],
  },
  {
    id: 'gizmosql-adbc-v2',
    title: m.m030,
    excerpt: m.m031,
    date: '2026-07-29',
    category: 'Release',
    image: '/adbc-drivers-badge.svg',
    images: ['/logos/gizmosql.png', '/adbc-drivers-badge.svg'],
    imageStyle: 'contain',
    link: '/news#gizmosql-adbc-v2',
    isExternal: false,
    features: [
      m.m032,
      m.m033,
      m.m034,
      m.m035
    ]
  },
  {
    id: 'gizmosql-v1-34-arrow-25',
    title: m.m036,
    excerpt: m.m037,
    date: '2026-07-22',
    category: 'Release',
    image: '/logos/arrow.png',
    images: ['/logos/gizmosql.png', '/logos/arrow.png'],
    imageStyle: 'contain',
    link: '/news#gizmosql-v1-34-arrow-25',
    isExternal: false,
    features: [
      m.m038,
      m.m039,
      m.m040,
      m.m041
    ]
  },
  {
    id: 'adbc-driver-gizmosql-connection-profiles',
    title: m.m042,
    excerpt: m.m043,
    date: '2026-07-03',
    category: 'Release',
    image: '/adbc-drivers-badge.svg',
    images: ['/logos/gizmosql.png', '/adbc-drivers-badge.svg'],
    imageStyle: 'contain',
    link: '/news#adbc-driver-gizmosql-connection-profiles',
    isExternal: false,
    features: [
      m.m044,
      m.m045,
      m.m046,
      m.m047
    ]
  },
  {
    id: 'gizmodata-community-slack',
    title: m.m048,
    excerpt: m.m049,
    date: '2026-06-30',
    category: 'Announcement',
    image: '/logos/slack.png',
    images: ['/logos/gizmosql.png', '/logos/slack.png'],
    imageStyle: 'contain',
    link: '/news#gizmodata-community-slack',
    isExternal: false,
    features: [m.m050, m.m051, m.m052, m.m053]
  },
  {
    id: 'ai-prosperity-podcast-torben-andersen',
    title: m.m054,
    excerpt: m.m055,
    date: '2026-06-27',
    category: 'Event',
    image: 'https://img.youtube.com/vi/0FLFhRJlIWU/maxresdefault.jpg',
    imageStyle: 'contain',
    link: 'https://youtu.be/0FLFhRJlIWU',
    isExternal: true,
    speaker: m.m056
  },
  {
    id: 'gizmosql-dbeaver-driver',
    title: m.m057,
    excerpt: m.m058,
    date: '2026-05-31',
    category: 'Announcement',
    image: '/logos/dbeaver.png',
    images: ['/logos/gizmosql.png', '/logos/dbeaver.png'],
    imageStyle: 'contain',
    link: 'https://github.com/dbeaver/dbeaver/releases/tag/26.1.0',
    isExternal: true,
    features: [
      m.m059,
      m.m060,
      m.m061,
      m.m062
    ]
  },
  {
    id: 'quack-jdbc-driver',
    title: m.m063,
    excerpt: m.m064,
    date: '2026-05-14',
    category: 'Release',
    image: '/logos/quack.svg',
    images: ['/logos/quack.svg', '/logos/jdbc.svg'],
    imageStyle: 'contain',
    link: '/news#quack-jdbc-driver',
    isExternal: false,
    features: [
      m.m065,
      m.m066,
      m.m067,
      m.m068
    ]
  },
  {
    id: 'adbc-driver-quack',
    title: m.m069,
    excerpt: m.m070,
    date: '2026-05-14',
    category: 'Release',
    image: '/logos/quack.svg',
    images: ['/logos/quack.svg', '/logos/arrow.png'],
    imageStyle: 'contain',
    link: '/news#adbc-driver-quack',
    isExternal: false,
    features: [
      m.m071,
      m.m072,
      m.m073,
      m.m074
    ]
  },
  {
    id: 'gizmosql-python-library',
    title: m.m075,
    excerpt: m.m076,
    date: '2026-05-08',
    category: 'Release',
    image: '/logos/python.svg',
    imageStyle: 'contain',
    link: '/news#gizmosql-python-library',
    isExternal: false,
    features: [
      m.m077,
      m.m078,
      m.m079,
      m.m080
    ]
  },
  {
    id: 'gizmosql-lts-channel',
    title: m.m081,
    excerpt: m.m082,
    date: '2026-05-08',
    category: 'Release',
    image: '/logos/gizmosql.png',
    imageStyle: 'contain',
    link: '/news#gizmosql-lts-channel',
    isExternal: false,
    features: [
      m.m083,
      m.m084,
      m.m085,
      m.m086
    ]
  },
  {
    id: 'gizmosql-qgis-plugin',
    title: m.m087,
    excerpt: m.m088,
    date: '2026-04-25',
    category: 'Release',
    image: '/logos/qgis.svg',
    imageStyle: 'contain',
    link: '/news#gizmosql-qgis-plugin',
    isExternal: false,
    features: [
      m.m089,
      m.m090,
      m.m091,
      m.m092
    ]
  },
  {
    id: 'gizmosql-ios-app',
    title: m.m093,
    excerpt: m.m094,
    date: '2026-04-14',
    category: 'Release',
    image: '/lovable-uploads/Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.png',
    imageStyle: 'contain',
    link: '/news#gizmosql-ios-app',
    isExternal: false,
    features: [
      m.m095,
      m.m096,
      m.m062,
      m.m097
    ]
  },
  {
    id: 'gizmosql-grafana-plugin',
    title: m.m098,
    excerpt: m.m099,
    date: '2026-03-11',
    category: 'Release',
    image: '/logos/grafana.svg',
    imageStyle: 'contain',
    link: '/news#gizmosql-grafana-plugin',
    isExternal: false,
    features: [
      m.m100,
      m.m062,
      m.m101,
      m.m102
    ]
  },
  {
    id: 'gizmosql-windows-x64',
    title: m.m103,
    excerpt: m.m104,
    date: '2026-03-01',
    category: 'Release',
    image: '/logos/windows.svg',
    imageStyle: 'contain',
    link: '/news#gizmosql-windows-x64',
    isExternal: false,
    features: [
      m.m105,
      m.m106,
      m.m107,
      m.m108
    ]
  },
  {
    id: 'gizmosql-powerbi-connector',
    title: m.m109,
    excerpt: m.m110,
    date: '2026-02-28',
    category: 'Release',
    image: '/logos/powerbi.svg',
    imageStyle: 'contain',
    link: '/news#gizmosql-powerbi-connector',
    isExternal: false,
    features: [
      m.m111,
      m.m112,
      m.m113,
      m.m114
    ]
  },
  {
    id: 'duckdb-developer-meeting-2026-01',
    title: m.m115,
    excerpt: m.m116,
    date: '2026-01-30',
    category: 'Event',
    image: '/duckdb-developer-meeting-1.svg',
    link: '/news#duckdb-developer-meeting-2026-01',
    isExternal: false,
    location: m.m117,
    speaker: m.m056
  },
  {
    id: 'gizmosql-enterprise-edition',
    title: m.m118,
    excerpt: m.m119,
    date: '2026-01-15',
    category: 'Announcement',
    image: '/gizmosql-enterprise-announcement.webp',
    imageStyle: 'contain',
    link: '/news#gizmosql-enterprise-edition',
    isExternal: false,
    features: [
      m.m120,
      m.m121,
      m.m122,
      m.m123,
      m.m124
    ]
  },
  {
    id: 'gizmosql-geospatial-support',
    title: m.m125,
    excerpt: m.m126,
    date: '2026-01-20',
    category: 'Release',
    image: '/gizmosql-geospatial.webp',
    imageStyle: 'contain',
    link: '/news#gizmosql-geospatial-support',
    isExternal: false,
    features: [
      m.m127,
      m.m128,
      m.m129,
      m.m130
    ]
  },
  {
    id: 'gizmoedge-hacker-news-number-1',
    title: m.m131,
    excerpt: m.m132,
    date: '2025-10-24',
    category: 'Announcement',
    image: '/hackernews-number1.jpeg',
    imageStyle: 'contain',
    link: '/news#gizmoedge-hacker-news-number-1',
    isExternal: false,
    features: [
      m.m133,
      m.m134,
      m.m135,
      m.m136
    ]
  }
  ];
};

export const newsItems = getNewsItems(messages.en);

const NewsSection = () => {
  const m = useMessages(messages);
  const newsItems = getNewsItems(m);
  const { locale, formatLocale } = useLocale();
  const getCategoryIcon = (category: NewsItem['category']) => {
    switch (category) {
      case 'Event':
        return <Mic className="h-3 w-3" />;
      case 'Announcement':
        return <Sparkles className="h-3 w-3" />;
      case 'Release':
        return <Rocket className="h-3 w-3" />;
      default:
        return null;
    }
  };

  const getCategoryColor = (category: NewsItem['category']) => {
    switch (category) {
      case 'Event':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-800 dark:text-purple-200';
      case 'Announcement':
        return 'bg-amber-100 text-amber-800 dark:bg-amber-800 dark:text-amber-200';
      case 'Release':
        return 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-200';
      default:
        return 'bg-gizmo-100 text-gizmo-800 dark:bg-gizmo-800 dark:text-gizmo-200';
    }
  };

  return (
    <section className="py-16 md:py-24 bg-white dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <SectionHeading withId anchor="news" className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            {m.m137}{' '}</SectionHeading>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            {m.m138}{' '}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          {newsItems.slice(0, 2).map((item) => {
            // Same convention as the blog cards: the image links to the story
            // (internal or external), not just the Learn More button.
            const imageBlock = (
              <div className={`aspect-video relative ${item.imageStyle === 'contain' ? 'bg-gray-100 dark:bg-gray-700' : 'bg-gradient-to-br from-gizmo-500 to-blue-600'}`}>
                {item.images && item.images.length > 1 ? (
                  <div className="w-full h-full flex items-center justify-center gap-4 p-4">
                    {item.images.map((src, i) => (
                      <LocaleImage
                        key={i}
                        src={src}
                        alt={`${item.title} logo ${i + 1}`}
                        width={200}
                        height={200}
                        className={`h-full max-h-full w-auto max-w-[48%] object-contain ${src === '/logos/gizmodata.png' ? 'rounded-xl bg-[#fff]' : ''}`}
                        loading="lazy"
                      />
                    ))}
                  </div>
                ) : (
                  <LocaleImage
                    src={item.image}
                    alt={item.title}
                    width={1600}
                    height={900}
                    className={`w-full h-full ${item.imageStyle === 'contain' ? 'object-contain p-4' : 'object-cover'}`}
                    loading="lazy"
                  />
                )}
              </div>
            );
            return (
            <div
              key={item.id}
              className="bg-gray-50 dark:bg-gray-800 rounded-xl shadow-lg hover:shadow-xl dark:hover:shadow-white/10 transition-shadow duration-300 overflow-hidden"
            >
              {!item.link ? (
                imageBlock
              ) : item.isExternal ? (
                <LocaleAnchor
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={item.title}
                  data-ga-cta="read_article"
                  data-ga-location="home_news"
                  data-ga-text={item.title}
                  className="block hover:opacity-90 transition-opacity"
                >
                  {imageBlock}
                </LocaleAnchor>
              ) : (
                <Link
                  to={item.link}
                  aria-label={item.title}
                  data-ga-cta="read_article"
                  data-ga-location="home_news"
                  data-ga-text={item.title}
                  className="block hover:opacity-90 transition-opacity"
                >
                  {imageBlock}
                </Link>
              )}
              <div className="p-6">
                <div className="flex items-center gap-4 mb-4">
                  <Badge className={`${getCategoryColor(item.category)} flex items-center gap-1`}>
                    {getCategoryIcon(item.category)}
                    {locale === 'pt-BR' ? { Event: 'Evento', Announcement: 'Anúncio', Release: 'Lançamento' }[item.category] : item.category}
                  </Badge>
                  <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                    <Calendar className="h-4 w-4 mr-1" />
                    {new Date(item.date).toLocaleDateString(formatLocale, {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      timeZone: 'UTC'
                    })}
                  </div>
                </div>

                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                  {item.title}
                </h3>

                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  {item.excerpt}
                </p>

                {item.location && (
                  <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-4">
                    <MapPin className="h-4 w-4 mr-2" />
                    {item.location}
                  </div>
                )}

                {item.features && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {item.features.map((feature) => (
                      <Badge key={feature} variant="outline" className="text-xs">
                        {feature}
                      </Badge>
                    ))}
                  </div>
                )}

                {item.link && (
                  item.isExternal ? (
                    <LocaleAnchor
                      href={item.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`${m.learnMoreAria}: ${item.title}`}
                    >
                      <Button variant="outline" className="w-full group">
                        {m.m139}{' '}<span className="sr-only">: {item.title}</span>
                        <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </LocaleAnchor>
                  ) : (
                    <Link to={item.link} aria-label={`${m.learnMoreAria}: ${item.title}`}>
                      <Button variant="outline" className="w-full group">
                        {m.m139}{' '}<span className="sr-only">: {item.title}</span>
                        <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </Link>
                  )
                )}
              </div>
            </div>
            );
          })}
        </div>

        <div className="text-center">
          <Link to="/news">
            <Button size="lg" className="bg-gizmo-600 hover:bg-gizmo-700 text-white">
              {m.m140}{' '}<ArrowRight className="h-5 w-5 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default NewsSection;
